// Доставки — заявките, добавени с редизайна: справката с анулираните, стоковите
// без фактура, плащането и анулирането с причина. Заварените (create, update,
// getDelivery…) са още в `inventory.ts` и се местят тук при всяка задача, която
// ги докосва. Договорът е в docs/cross-repo-contract.md.
import { api } from "./client";
import type {
  Delivery,
  DeliveryDocumentType,
  DeliveryPaymentMethod,
} from "./inventory";
import type { DocumentStatusFilter } from "./inventoryPostings";

export type DeliveriesReportParams = {
  startDate: string;
  endDate: string;
  status?: DocumentStatusFilter;
  documentType?: DeliveryDocumentType;
};

// Справката „Доставки" (по избор и анулираните). Стар BE игнорира `status`.
export async function getDeliveriesReport(
  params: DeliveriesReportParams,
): Promise<Delivery[]> {
  const res = await api.get<Delivery[]>("/inventory/deliveries", { params });
  return res.data;
}

// Стоковите разписки без фактура — без период (чакат фактура от доставчика).
// Стар BE не знае филтъра → връща празно (виж useUninvoicedNotes).
export async function getUninvoicedNotes(supplierId?: number | null): Promise<Delivery[]> {
  const res = await api.get<Delivery[]>("/inventory/deliveries", {
    params: {
      documentType: "note",
      uninvoiced: true,
      ...(supplierId != null ? { supplierId } : {}),
    },
  });
  // Стар BE връща всички доставки за подразбиращия се период — филтрираме
  // по полетата, които той не праща, за да не излезе „всичко е стокова".
  return res.data.filter(
    (delivery) =>
      delivery.documentType === "note" &&
      delivery.invoiceId == null &&
      !delivery.closedWithoutInvoiceAt,
  );
}

// Лек ред за плащане (без редовете на документа)
export interface PayableDelivery {
  id: number;
  date: string;
  createdAt: string | null;
  documentType: DeliveryDocumentType | null;
  invoiceNumber: string | null;
  supplierId: number | null;
  supplierName: string | null;
  warehouseId: number | null;
  warehouseName: string | null;
  totalValueNoVat: number;
  totalValueWithVat: number;
  createdByName: string | null;
  linkedNoteIds: number[];
  // Стоковите на фактурата, платени отделно — не са в сумата (стар BE: липсва)
  paidNoteIds?: number[];
  note?: string | null; // бележката на документа (стар BE: липсва)
}

export async function getPayableDeliveries(): Promise<PayableDelivery[]> {
  const res = await api.get<PayableDelivery[]>("/inventory/deliveries/payable");
  return res.data;
}

export type PayDeliveriesPayload = {
  ids: number[];
  paidAt: string;
  method: DeliveryPaymentMethod;
  reference: string | null;
};

export async function payDeliveries(payload: PayDeliveriesPayload): Promise<number> {
  const res = await api.post<{ updated: number }>("/inventory/deliveries/pay", payload);
  return res.data.updated;
}

export async function unpayDeliveries(ids: number[]): Promise<number> {
  const res = await api.post<{ updated: number }>("/inventory/deliveries/unpay", { ids });
  return res.data.updated;
}

// Стоковите разписки (service_delivery_notes.py в BE): изваждане от фактурата,
// приключване без фактура (по избор и плащане) и връщане за фактуриране.
export async function detachNotesFromInvoice(ids: number[]): Promise<number> {
  const res = await api.post<{ updated: number }>(
    "/inventory/deliveries/detach-from-invoice", { ids },
  );
  return res.data.updated;
}

export type CloseWithoutInvoicePayload = {
  ids: number[];
  reason: string | null;
  payment: Omit<PayDeliveriesPayload, "ids"> | null;
};

export async function closeNotesWithoutInvoice(
  payload: CloseWithoutInvoicePayload,
): Promise<number> {
  const res = await api.post<{ updated: number }>(
    "/inventory/deliveries/close-without-invoice", payload,
  );
  return res.data.updated;
}

export async function reopenNotesForInvoice(ids: number[]): Promise<number> {
  const res = await api.post<{ updated: number }>(
    "/inventory/deliveries/reopen-for-invoice", { ids },
  );
  return res.data.updated;
}

// Анулиране с причина (незадължителна; стар BE я игнорира)
export async function annulDelivery(id: number, annulReason: string | null): Promise<void> {
  await api.delete(`/inventory/deliveries/${id}`, {
    data: annulReason ? { annulReason } : undefined,
  });
}
